import { forOwn } from 'lodash';
import { createLogger } from 'shared/services/logger';
import { createMarkupRegion } from './markup-region-factory';

const logger = createLogger('us:annotations:markup-service');

// The components in this folder provide the ability to render regions with analysis markup; this class is the main entry point.

// High level overview:
// The term "Markup" in this context refers to regions generated when image analysis is run on a region (or slide).
// This service creates a container div on top of the Viewport div and stores it's markup regions inside it.
// Any time a region is added or removed by the annotations service, this service will create and render or destroy a markup
// region. The rendering method varies depending on the size of the markup image. Because of this a factory is used to create
// markup regions. Regions must conform to the following API:
//   IMarkupRegion = {
//      fragment: DOMNode,      // Container for region's tiles
//      render: () => {},       // Performs initial DOM setup of the region. Called once per region. TODO: Rename to init?
//      paint:  () => {}        // Paints the region. Called when the viewport is moved or zoom changes.
//  }

export function MarkupService (uScope, viewport) {
  const self = this;
  const layers = {};
  let container;

  self.init = () => {
    container = document.createElement('div');
    container.classList.add('us-markup-container');
    viewport.renderer.addLayer({
      container,
      paint () {
        getAllMarkups().forEach(markup => markup.paint());
      }
    });

    viewport.events.on('us:annotations:region-added', handleRegionAdded);
    viewport.events.on('us:annotations:region-removed', handleRegionRemoved);
    viewport.events.on('us:annotations:layer-removed', handleLayerRemoved);
    viewport.events.on('us:annotations:layer-reinitialized', handleLayerReinitialized);
  };

  self.showRegions = (displayIds, layerId) => {
    if (layers[layerId]) {
      displayIds.forEach(id => self.showRegion(id, layerId));
    } else {
      logger.warn('No regions found for layerId', layerId);
    }
  };

  self.showRegion = (displayId, layerId) => {
    exec('show', displayId, layerId);
  };

  self.hideRegion = (displayId, layerId) => {
    exec('hide', displayId, layerId);
  };

  self.hideAllRegions = () => {
    forOwn(layers, (layer, layerId) => {
      Object.keys(layer).forEach((displayId) => {
        self.hideRegion(displayId, layerId);
      });
    });
  };

  function exec (action, displayId, layerId) {
    const layer = layers[layerId];
    if (layer && layer[displayId]) {
      layer[displayId][action]();
    } else {
      logger.warn(action, ': Region', displayId, 'not found for layerId', layerId, 'or layer was not found.');
    }
  }

  const handleRegionAdded = region => {
    if (!region.MarkupImageId) {
      return;
    }

    const markup = createMarkupRegion(region, viewport);

    const layerId = region.layerId;
    if (!layers[layerId]) {
      layers[layerId] = {};
    }
    layers[layerId][region.displayId] = markup;

    markup.render();
    container.appendChild(markup.fragment);
  };

  const handleRegionRemoved = region => {
    if (!region.layerId || !region.displayId) {
      logger.warn('Cannot remove a markup without both a layerId and a displayId');
      return;
    }
    const layer = layers[region.layerId];
    const markup = layer ? layer[region.displayId] : null;

    if (layer && markup) {
      // Note that if a layer is deleted the "regionRemoved" event is still fired so we don't log warnings.
      delete layer[region.displayId];
      markup.remove();
    }
  };

  const handleLayerRemoved = layerId => {
    const layer = layers[layerId];
    forOwn(layer, (markup) => markup.remove());
  };

  const handleLayerReinitialized = ({imageId, layer, regions}) => {
    layer = layers[layer.id];
    if (layer) {
      Object.values(regions).forEach(r => {
        const markup = getMarkup(r);
        markup && markup.markAsDirty();
      });
    }
  };

  function getMarkup (region) {
    const layer = layers[region.layerId];
    const markup = layer ? layer[region.displayId] : null;
    return markup || null;
  }

  function getAllMarkups () {
    const markups = [];
    forOwn(layers, (layer) => {
      forOwn(layer, (markup) => {
        markups.push(markup);
      });
    });
    return markups;
  }
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/image-analysis/markup/markup-service.js